import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  Archive,
  Lock,
  Share2,
  Copy,
  ShieldCheck,
  History,
  Download,
  FileText,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Quality, downloadAuthed } from "@/lib/api";

/**
 * QualityTimeline — the Evidence Engine's browsing/sharing surface.
 *
 * Three inner tabs:
 *   • Timeline: pick a date → see its frozen snapshot (or the current live state
 *     if nothing has been frozen yet). Users can freeze/sign snapshots from here.
 *   • Share links: create expiring read-only URLs for external stakeholders.
 *   • Audit trail: see every recorded change to QA data with who/when/what.
 */
export default function QualityTimeline() {
  const [subTab, setSubTab] = useState("timeline");
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(todayIso);
  const [timeline, setTimeline] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTimeline = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const [tl, snaps] = await Promise.all([
        Quality.timeline(date),
        Quality.listSnapshots({ start: date, end: date }),
      ]);
      setTimeline(tl);
      setSnapshots(snaps);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [date]);

  const loadShareLinks = useCallback(async () => {
    try {
      const rows = await Quality.listShareLinks({});
      setShareLinks(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load share links");
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const rows = await Quality.audit({ limit: 200 });
      setAudit(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit trail");
    }
  }, []);

  useEffect(() => {
    if (subTab === "timeline") loadTimeline();
    if (subTab === "share") loadShareLinks();
    if (subTab === "audit") loadAudit();
  }, [subTab, loadTimeline, loadShareLinks, loadAudit]);

  return (
    <div className="p-6 space-y-4" data-testid="quality-timeline-root">
      <div className="flex gap-1 bg-white/60 rounded-md p-1 border border-[#e2e8f0] w-fit">
        {[
          { id: "timeline", label: "Timeline & Snapshots", Icon: Archive },
          { id: "share", label: "Share Links", Icon: Share2 },
          { id: "audit", label: "Audit Trail", Icon: History },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            data-testid={`timeline-sub-${id}`}
            className={[
              "px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded transition-colors flex items-center gap-1.5",
              subTab === id
                ? "bg-[#0284c7] text-white"
                : "text-[#475569] hover:bg-white",
            ].join(" ")}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {subTab === "timeline" && (
        <TimelineTab
          date={date}
          setDate={setDate}
          timeline={timeline}
          snapshots={snapshots}
          loading={loading}
          onReload={loadTimeline}
        />
      )}
      {subTab === "share" && (
        <ShareTab links={shareLinks} onReload={loadShareLinks} />
      )}
      {subTab === "audit" && <AuditTab rows={audit} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function TimelineTab({ date, setDate, timeline, snapshots, loading, onReload }) {
  const [signInitials, setSignInitials] = useState("");

  const freeze = async () => {
    try {
      await Quality.createSnapshot({ date, kind: "daily", label: "" });
      toast.success("Snapshot frozen. Nothing on this day can be lost anymore.");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freeze failed");
    }
  };

  const signSnap = async (id) => {
    if (!signInitials.trim()) {
      toast.error("Enter your initials / name to sign.");
      return;
    }
    try {
      await Quality.signSnapshot(id, signInitials.trim());
      toast.success(`Signed by ${signInitials.trim()}`);
      setSignInitials("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    }
  };

  const shareSnap = async (snap) => {
    try {
      const link = await Quality.createShareLink({
        resource: "snapshot",
        ref: snap.id,
        label: `QAPI snapshot ${snap.date} (${snap.kind})`,
        expires_at: "",
      });
      const url = Quality.sharedUrl(link.token);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link created & copied to clipboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share link failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
        <div>
          <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">
            Browse date
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 h-9 border-[#e2e8f0] w-44"
            data-testid="timeline-date"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            onClick={freeze}
            disabled={loading}
            data-testid="timeline-freeze"
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
          >
            <Lock className="w-4 h-4 mr-1.5" />
            Freeze this day
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadAuthed(Quality.digestXlsxUrl(date), `qapi-daily-digest-${date}.xlsx`)}
            data-testid="timeline-digest-xlsx"
            className="border-[#e2e8f0]"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Digest XLSX
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadAuthed(Quality.digestPdfUrl(date), `qapi-daily-digest-${date}.pdf`)}
            data-testid="timeline-digest-pdf"
            className="border-[#e2e8f0]"
          >
            <FileText className="w-4 h-4 mr-1.5" />
            Digest PDF
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#e2e8f0] overflow-hidden bg-white">
        <div className="px-4 py-3 bg-[#f0f9ff] border-b border-[#e2e8f0] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0284c7]" />
          <span className="font-semibold text-[#0f172a]">
            {format(parseISO(date), "EEEE, MMM d, yyyy")}
          </span>
          {timeline?.is_frozen ? (
            <span className="ml-2 px-2 py-0.5 rounded bg-[#0284c7] text-white text-[10px] font-semibold uppercase tracking-wide">
              Frozen
            </span>
          ) : (
            <span className="ml-2 px-2 py-0.5 rounded bg-[#fcf3e1] text-[#7a5a1a] text-[10px] font-semibold uppercase tracking-wide">
              Live · not yet frozen
            </span>
          )}
        </div>
        <div className="p-4">
          {timeline?.is_frozen ? (
            <FrozenView snap={timeline.snapshot} />
          ) : (
            <LiveView payload={timeline?.payload} />
          )}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold tracking-widest uppercase text-[#475569] mb-2">
          All snapshots on this date
        </h4>
        {snapshots.length === 0 && (
          <div className="text-sm text-[#475569] italic">No snapshots yet — click <em>Freeze this day</em> to archive the current state.</div>
        )}
        <div className="space-y-2">
          {snapshots.map((s) => (
            <div
              key={s.id}
              className="p-3 rounded border border-[#e2e8f0] bg-white flex items-center gap-3 flex-wrap"
              data-testid={`snapshot-row-${s.id}`}
            >
              <Archive className="w-4 h-4 text-[#0284c7]" />
              <div className="flex-1 min-w-[240px]">
                <div className="text-sm font-semibold text-[#0f172a]">
                  {s.kind === "daily" ? "Daily freeze" : s.kind} · {s.taken_at?.slice(0, 19).replace("T", " ")}
                </div>
                <div className="text-xs text-[#475569]">
                  by {s.taken_by || "system"} · sha256 <code className="font-mono">{s.sha256?.slice(0, 12)}…</code>
                  {s.signed_by && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-[#f0f9ff] text-[#0284c7]">
                      ✍ signed by {s.signed_by}
                    </span>
                  )}
                </div>
              </div>
              {!s.signed_by && (
                <div className="flex items-center gap-1">
                  <Input
                    placeholder="Your initials"
                    value={signInitials}
                    onChange={(e) => setSignInitials(e.target.value)}
                    className="w-32 h-8 border-[#e2e8f0]"
                    data-testid={`sign-initials-${s.id}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => signSnap(s.id)}
                    data-testid={`sign-btn-${s.id}`}
                    className="border-[#e2e8f0]"
                  >
                    Sign
                  </Button>
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => shareSnap(s)}
                data-testid={`share-btn-${s.id}`}
                className="border-[#e2e8f0]"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" />
                Share link
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function FrozenView({ snap }) {
  const payload = snap?.payload || {};
  const day = payload.day || {};
  const indicators = payload.indicators || [];
  const checks = payload.checks || [];
  const tasks = payload.tasks || [];
  const caps = payload.caps || [];
  const patientsCount = ((day.patients || []).length) + ((day.unassigned || []).length);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Patients" value={patientsCount} />
      <Stat label="Indicators" value={indicators.length} />
      <Stat label="Checks" value={checks.length} />
      <Stat label="Open tasks" value={tasks.filter(t => t.status !== "completed").length} />
      <Stat label="Active CAPs" value={caps.filter(c => c.status !== "completed").length} />
      <Stat label="Discharges" value={(day.discharges || []).length} />
      <Stat label="Frozen at" value={(snap.taken_at || "").slice(0, 19).replace("T", " ")} small />
      <Stat label="Hash" value={(snap.sha256 || "").slice(0, 10) + "…"} small mono />
    </div>
  );
}

function LiveView({ payload }) {
  if (!payload) return <div className="text-sm text-[#475569] italic">Loading…</div>;
  const day = payload.day || {};
  const patientsCount = ((day.patients || []).length) + ((day.unassigned || []).length);
  return (
    <div className="space-y-3">
      <div className="text-sm text-[#475569]">
        This day has not been frozen yet. Once you freeze it, this exact state will be
        permanently preserved — even if patients, tasks, or CAPs change later.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Patients (live)" value={patientsCount} />
        <Stat label="Indicators" value={(payload.indicators || []).length} />
        <Stat label="Checks today" value={(payload.checks || []).length} />
        <Stat label="Open tasks" value={(payload.tasks || []).filter(t => t.status !== "completed").length} />
      </div>
    </div>
  );
}

function Stat({ label, value, small, mono }) {
  return (
    <div className="p-3 rounded border border-[#e2e8f0] bg-[#f8fafc]">
      <div className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">{label}</div>
      <div className={[
        "font-heading text-[#0f172a] mt-0.5",
        small ? "text-sm" : "text-2xl",
        mono ? "font-mono" : "",
      ].join(" ")}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ShareTab({ links, onReload }) {
  const [resource, setResource] = useState("digest");
  const [ref, setRef] = useState(new Date().toISOString().slice(0, 10));
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const create = async () => {
    try {
      const link = await Quality.createShareLink({
        resource,
        ref,
        label,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : "",
      });
      const url = Quality.sharedUrl(link.token);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Link created & copied to clipboard.");
      setLabel("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create share link");
    }
  };

  const revoke = async (id) => {
    try {
      await Quality.revokeShareLink(id);
      toast.success("Link revoked.");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyLink = async (link) => {
    await navigator.clipboard.writeText(Quality.sharedUrl(link.token));
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-md border border-[#e2e8f0] bg-[#f8fafc] space-y-3">
        <h4 className="text-xs font-semibold tracking-widest uppercase text-[#475569]">Create a new share link</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Resource</Label>
            <select
              value={resource}
              onChange={(e) => setResource(e.target.value)}
              data-testid="share-resource"
              className="mt-1 h-9 w-full border border-[#e2e8f0] rounded px-2 text-sm bg-white"
            >
              <option value="digest">Daily digest</option>
              <option value="monthly">Monthly packet</option>
              <option value="snapshot">Specific snapshot</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">
              {resource === "digest" ? "Date (YYYY-MM-DD)"
                : resource === "monthly" ? "Month (YYYY-MM)"
                : "Snapshot ID"}
            </Label>
            <Input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              data-testid="share-ref"
              className="mt-1 h-9 border-[#e2e8f0]"
              placeholder={resource === "monthly" ? "2026-02" : resource === "digest" ? "2026-02-15" : "snap-uuid"}
            />
          </div>
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="share-label"
              className="mt-1 h-9 border-[#e2e8f0]"
              placeholder="e.g. Feb QAPI packet for board"
            />
          </div>
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Expires (optional)</Label>
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="share-expires"
              className="mt-1 h-9 border-[#e2e8f0]"
            />
          </div>
        </div>
        <Button
          onClick={create}
          data-testid="share-create"
          className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
        >
          <Share2 className="w-4 h-4 mr-1.5" />
          Create link
        </Button>
      </div>

      <div>
        <h4 className="text-xs font-semibold tracking-widest uppercase text-[#475569] mb-2">Active links</h4>
        {links.length === 0 && (
          <div className="text-sm text-[#475569] italic">No share links yet.</div>
        )}
        <div className="space-y-2">
          {links.map((l) => (
            <div
              key={l.id}
              className="p-3 rounded border border-[#e2e8f0] bg-white flex items-center gap-3 flex-wrap"
              data-testid={`share-row-${l.id}`}
            >
              <div className="flex-1 min-w-[260px]">
                <div className="text-sm font-semibold text-[#0f172a]">
                  {l.resource} · {l.ref}
                  {l.label && <span className="ml-2 text-[#475569] font-normal">— {l.label}</span>}
                </div>
                <div className="text-xs text-[#475569] font-mono truncate max-w-full">
                  /shared/{l.token}
                </div>
                <div className="text-[10px] text-[#475569] mt-0.5">
                  Views: {l.views || 0}
                  {l.last_viewed_at && ` · last viewed ${l.last_viewed_at.slice(0, 19).replace("T", " ")}`}
                  {l.expires_at && ` · expires ${l.expires_at.slice(0, 16).replace("T", " ")}`}
                  {l.revoked && <span className="ml-2 text-[#b94a48]">REVOKED</span>}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyLink(l)} className="border-[#e2e8f0]" data-testid={`copy-${l.id}`}>
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
              {!l.revoked && (
                <Button size="sm" variant="outline" onClick={() => revoke(l.id)} className="border-[#e2e8f0] text-[#b94a48]" data-testid={`revoke-${l.id}`}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AuditTab({ rows }) {
  const iconFor = (action) => {
    if (action === "create") return <CheckCircle2 className="w-3.5 h-3.5 text-[#0284c7]" />;
    if (action === "delete") return <XCircle className="w-3.5 h-3.5 text-[#b94a48]" />;
    return <History className="w-3.5 h-3.5 text-[#475569]" />;
  };
  return (
    <div className="rounded-md border border-[#e2e8f0] overflow-hidden bg-white">
      <div className="grid grid-cols-[120px_120px_120px_1fr_180px] gap-2 px-3 py-2 text-[10px] font-semibold tracking-widest uppercase text-[#475569] bg-[#f8fafc] border-b border-[#e2e8f0]">
        <div>Timestamp</div>
        <div>Entity</div>
        <div>Action</div>
        <div>Summary</div>
        <div>User</div>
      </div>
      {rows.length === 0 && (
        <div className="p-6 text-sm text-[#475569] italic text-center">No audit events yet — audit entries appear here as directors, indicators, tasks, and CAPs are edited.</div>
      )}
      <div className="max-h-[520px] overflow-auto">
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`audit-row-${r.id}`}
            className="grid grid-cols-[120px_120px_120px_1fr_180px] gap-2 px-3 py-2 border-b border-[#f4f2ea] text-sm text-[#0f172a] items-center"
          >
            <div className="text-xs text-[#475569]">{r.ts?.slice(0, 19).replace("T", " ")}</div>
            <div className="text-xs uppercase tracking-wider text-[#475569]">{r.entity_type}</div>
            <div className="flex items-center gap-1 text-xs">{iconFor(r.action)}{r.action}</div>
            <div className="text-sm">{r.summary || "—"}</div>
            <div className="text-xs text-[#475569] truncate">{r.user_email || "system"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
